import React, { useState } from 'react';
import { Plus, Trash2, Package, GripVertical, Star } from 'lucide-react';
import EditableText from './EditableText';

/**
 * Variants editor — inline in ProductAdminCard. Each row is one Pack Size
 * (or any other packaging variant). When `show_in_grid` is on the variant
 * materialises as its own tile in category/brand grids; otherwise it only
 * appears as a selector on the parent PDP.
 *
 * SAFETY: Products with zero variants behave EXACTLY as before — this editor
 * only appends. Removing all variants restores the plain single-card behaviour.
 */
const genId = () => 'v_' + Math.random().toString(36).slice(2, 8);

const VariantsEditor = ({ product, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const variants = product.variants || [];

  const patch = (nextList) => onSave(nextList.length > 0 ? nextList : null);

  const addVariant = () => {
    const next = [...variants, {
      id: genId(),
      name: 'New pack size',
      price: product.price || 0,
      sku: '',
      stock: null,
      is_default: false,
      show_in_grid: true,
    }];
    patch(next);
    setExpanded(true);
  };

  const updateVariant = (idx, patchObj) => {
    const next = variants.map((v, i) => i === idx ? { ...v, ...patchObj } : v);
    // Only one variant can be `is_default` at a time — the pre-selection.
    if (patchObj.is_default) {
      next.forEach((v, i) => { if (i !== idx) v.is_default = false; });
    }
    patch(next);
  };

  const removeVariant = (idx) => {
    if (!window.confirm('Remove this variant? Existing orders keep their snapshotted variant name/price.')) return;
    patch(variants.filter((_, i) => i !== idx));
  };

  return (
    <div className="border-t border-neutral-200 pt-2">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="text-[11px] uppercase text-[#F46A25] font-bold flex items-center gap-1"
          data-testid={`variants-toggle-${product.id}`}
        >
          <Package className="w-3 h-3" />
          Variants / pack sizes {variants.length > 0 && <span className="ml-1 text-neutral-500 normal-case font-normal">({variants.length})</span>}
        </button>
        <button
          type="button"
          onClick={addVariant}
          data-testid={`variants-add-${product.id}`}
          className="text-[11px] font-semibold text-[#F46A25] hover:text-[#e05a15] flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add variant
        </button>
      </div>

      {(expanded || variants.length > 0) && variants.length === 0 && (
        <div className="text-[11px] text-neutral-500 italic py-1">
          No variants yet — this product ships as a single unit only.
        </div>
      )}

      {variants.length > 0 && (
        <div className="space-y-2">
          {variants.map((v, idx) => (
            <div
              key={v.id}
              className="border border-neutral-200 rounded-sm p-2 bg-neutral-50"
              data-testid={`variant-row-${product.id}-${v.id}`}
            >
              <div className="flex items-start gap-2">
                <GripVertical className="w-4 h-4 text-neutral-400 mt-1 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Variant name (shown to customer)</span>
                    <EditableText
                      value={v.name}
                      onSave={(val) => updateVariant(idx, { name: val })}
                      placeholder="e.g. 10 Box Pack"
                      className="font-semibold"
                      data-testid={`variant-name-${v.id}`}
                    />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Price (£, ex VAT)</span>
                    <EditableText
                      value={v.price}
                      type="number"
                      prefix="£"
                      onSave={(val) => updateVariant(idx, { price: Number(val) || 0 })}
                      data-testid={`variant-price-${v.id}`}
                    />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase text-neutral-500 font-semibold block">SKU (optional)</span>
                    <EditableText
                      value={v.sku || ''}
                      onSave={(val) => updateVariant(idx, { sku: val || null })}
                      placeholder="Parent SKU"
                      data-testid={`variant-sku-${v.id}`}
                    />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Stock (optional)</span>
                    <EditableText
                      value={v.stock ?? ''}
                      type="number"
                      onSave={(val) => updateVariant(idx, { stock: val === '' || val == null ? null : Number(val) })}
                      placeholder="Uses parent"
                      data-testid={`variant-stock-${v.id}`}
                    />
                  </div>
                  <div className="col-span-3 flex flex-wrap items-center gap-3 pt-1">
                    <label className="flex items-center gap-1.5 text-[11px] text-[#12263A] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!v.show_in_grid}
                        onChange={e => updateVariant(idx, { show_in_grid: e.target.checked })}
                        data-testid={`variant-show-in-grid-${v.id}`}
                      />
                      Show as own tile in grid
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-[#12263A] cursor-pointer">
                      <input
                        type="radio"
                        name={`variant-default-${product.id}`}
                        checked={!!v.is_default}
                        onChange={() => updateVariant(idx, { is_default: true })}
                        data-testid={`variant-is-default-${v.id}`}
                      />
                      <Star className={`w-3 h-3 ${v.is_default ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-400'}`} /> Pre-select on PDP
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeVariant(idx)}
                  aria-label="Remove variant"
                  data-testid={`variant-remove-${v.id}`}
                  className="text-neutral-500 hover:text-red-600 mt-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VariantsEditor;
